import { useId } from 'react';
import { Eye, Heart, MessageCircle, Share2 } from 'lucide-react';
import type { Post, PostMetrics } from '@mg/shared';
import { Card } from '../../components/ui/Card';
import { formatMetric, metricsPending } from '../../lib/display';

const METRICS: { key: keyof Omit<PostMetrics, 'fetchedAt'>; Icon: typeof Eye; label: string }[] = [
  { key: 'views', Icon: Eye, label: 'Views' },
  { key: 'likes', Icon: Heart, label: 'Likes' },
  { key: 'comments', Icon: MessageCircle, label: 'Comments' },
  { key: 'shares', Icon: Share2, label: 'Shares' },
];

/**
 * How a published post is doing on its platform: views, likes, comments and shares, from the
 * metrics the analytics sweep stores on the post (one post per destination, so these are the
 * selected destination's). A metric the platform does not report is a dash, never 0; numbers
 * still on their way after publishing shimmer, as they do on every other metric in the app.
 * Renders nothing for a post that has not gone out.
 */
export function PostPerformance({ post }: { post: Pick<Post, 'publishedAt' | 'metrics'> }) {
  const titleId = useId();
  if (post.publishedAt == null) return null;
  const pending = metricsPending(post);

  return (
    <Card>
      <section aria-labelledby={titleId} className="px-5 py-4">
        <h3 id={titleId} className="text-sm font-semibold text-ink">
          Post performance
        </h3>
        {/* The row is four labels without numbers while the numbers are pending, so a screen reader
            gets the state instead, the word the app's other pending metrics use. */}
        {pending && <p className="sr-only">Pending</p>}
        {/* Each dot hangs in the gap before its metric, and the row is pulled one gap left of a box
            that clips only its left edge. Wherever the row wraps, the dot in front of a line's first
            metric falls in that strip, so no line ever starts with a dot - and at a large text size
            a metric wider than the box runs on rather than being cut. */}
        <div className="mt-2 [clip-path:inset(0_-100vw_0_0)]" aria-hidden={pending || undefined}>
          <div className="-ml-5 flex flex-wrap items-center gap-y-2 text-sm">
            {METRICS.map(({ key, Icon, label }) => {
              const value = post.metrics?.[key] ?? null;
              return (
                <span key={key} className="relative ml-5 inline-flex items-center gap-1.5">
                  <span className="absolute -left-3 text-ink-ghost" aria-hidden="true">
                    ·
                  </span>
                  <Icon className="h-4 w-4 text-ink-faint" aria-hidden="true" />
                  {pending ? (
                    <span className="h-4 w-8 rounded bg-chip motion-safe:animate-pulse" aria-hidden="true" />
                  ) : value == null ? (
                    // Muted and unweighted, as everywhere else in the app, so it never reads as a
                    // number. It is for the eye; a screen reader hears "Views not available".
                    <span className="tabular-nums text-ink-muted" aria-hidden="true">
                      {formatMetric(null)}
                    </span>
                  ) : (
                    <span className="font-semibold tabular-nums text-ink">{formatMetric(value)}</span>
                  )}
                  <span className="text-ink-muted">{label}</span>
                  {!pending && value == null && <span className="sr-only">not available</span>}
                </span>
              );
            })}
          </div>
        </div>
      </section>
    </Card>
  );
}
